/**
 * Short-lived app sessions (ADR-0005), cached per app so opening an app or
 * loading its widgets doesn't wait on the server each time (ADR-0024). A cached
 * session is used while it has more than a minute left; it's renewed in the
 * background before that and at once when the server says it expired.
 */
import { api, apiBase, errorFrom, withQuery } from "./api";
import type { AppSession } from "./bridge";
import { load, save } from "./storage";

interface Cached {
  session: AppSession;
  expires: number;
}

const KEY = () => `omni.appSessions:${apiBase()}`;
const cache = new Map<string, Cached>(Object.entries(load<Record<string, Cached>>(KEY(), {})));
const pending = new Map<string, Promise<AppSession>>();

function persist() {
  const live: Record<string, Cached> = {};
  for (const [id, c] of cache) if (c.expires > Date.now()) live[id] = c;
  save(KEY(), live);
}

/** A session with more than a minute left, without a network call. */
export function cachedAppSession(appId: string): AppSession | null {
  const hit = cache.get(appId);
  return hit && hit.expires > Date.now() + 60_000 ? hit.session : null;
}

export function appSession(appId: string, fresh = false): Promise<AppSession> {
  const hit = cachedAppSession(appId);
  if (!fresh && hit) {
    // Renew quietly when it's getting old, so the next call is still instant
    const left = cache.get(appId)!.expires - Date.now();
    if (left < 10 * 60_000 && !pending.has(appId)) void appSession(appId, true).catch(() => {});
    return Promise.resolve(hit);
  }
  if (!pending.has(appId)) {
    pending.set(
      appId,
      api<AppSession>(`/api/apps/${appId}/session`, { method: "POST" })
        .then((s) => {
          cache.set(appId, { session: s, expires: Date.now() + s.expires_in * 1000 });
          persist();
          return s;
        })
        .finally(() => pending.delete(appId)),
    );
  }
  return pending.get(appId)!;
}

/** GET /api/m/<app><path> as that app. */
export async function appGet<T>(appId: string, path: string, query?: Record<string, string>): Promise<T> {
  const call = async (s: AppSession) =>
    fetch(`${apiBase()}${withQuery(`/api/m/${appId}${path}`, query)}`, { headers: { Authorization: `Bearer ${s.token}` } });
  let res = await call(await appSession(appId));
  if (res.status === 401) res = await call(await appSession(appId, true));
  if (!res.ok) throw await errorFrom(res);
  return (await res.json()) as T;
}

export function forgetSessions() {
  cache.clear();
  save(KEY(), null);
}
